import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Tabs,
    Tab,
    IconButton,
    Typography,
    Grid,
    InputBase,
    Paper
} from '@mui/material';
import {
    X,
    Clock,
    Plus,
    FunctionSquare,
    Type as LettersIcon,
    Layers,
    ArrowLeftRight,
    Minus,
    Parentheses,
    Grid3X3,
    Ruler,
    Search,
    Divide
} from 'lucide-react';
import './SymbolLibrary.css';

interface SymbolItem {
    latex: string;
    display: string;
    keywords?: string[];
}

interface SymbolSection {
    name: string;
    items: SymbolItem[];
}

const symbolData: Record<string, { icon: React.ReactElement, sections: SymbolSection[] }> = {
    'Recent': {
        icon: <Clock size={20} strokeWidth={1.5} />,
        sections: [{
            name: 'Recently Used',
            items: [
                { latex: 'x', display: 'x' },
                { latex: 'y', display: 'y' },
                { latex: 'f(x)', display: 'f(x)' },
            ]
        }]
    },
    'Basic': {
        icon: <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Plus size={16} strokeWidth={2} />
            <Divide size={12} strokeWidth={2} style={{ position: 'absolute', bottom: -2, right: -2 }} />
        </Box>,
        sections: [{
            name: 'Basic Operations',
            items: [
                { latex: '\\frac{#?}{#?}', display: '□/□' },
                { latex: '#?^{#?}', display: 'x^□' },
                { latex: '#?_{#?}', display: 'x_□' },
                { latex: '\\binom{#?}{#?}', display: '(□/□)' },
                { latex: '\\sqrt{#?}', display: '√□' },
                { latex: '\\sqrt[#?]{#?}', display: 'ⁿ√' },
                { latex: '=', display: '=' },
                { latex: '+', display: '+' },
                { latex: '-', display: '-' },
                { latex: '\\pm', display: '±' },
                { latex: '\\mp', display: '∓' },
                { latex: '\\cdot', display: '·' },
                { latex: '\\times', display: '×' },
                { latex: '\\div', display: '÷' },
                { latex: '<', display: '<' },
                { latex: '>', display: '>' },
                { latex: '\\leq', display: '≤' },
                { latex: '\\geq', display: '≥' },
                { latex: '\\neq', display: '≠' },
                { latex: '\\approx', display: '≈' },
                { latex: '\\equiv', display: '≡' },
                { latex: '\\propto', display: '∝' },
                { latex: '\\ll', display: '≪' },
                { latex: '\\gg', display: '≫' },
            ]
        }, {
            name: 'Advanced Operations',
            items: [
                { latex: '\\oplus', display: '⊕' },
                { latex: '\\otimes', display: '⊗' },
                { latex: '\\circ', display: '∘' },
                { latex: '\\bullet', display: '∙' },
                { latex: '\\ast', display: '∗' },
                { latex: '\\perp', display: '⊥' },
                { latex: '\\parallel', display: '∥' },
                { latex: '\\angle', display: '∠' },
                { latex: '\\triangle', display: '△' },
                { latex: '\\cong', display: '≅' },
                { latex: '\\sim', display: '∼' },
                { latex: '\\simeq', display: '≃' },
                { latex: '\\dagger', display: '†' },
                { latex: '\\aleph', display: 'ℵ' },
            ]
        }]
    },
    'Functions': {
        icon: <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 600, fontFamily: 'serif', fontStyle: 'italic' }}>f</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>x</Typography>
        </Box>,
        sections: [
            {
                name: 'Trigonometry',
                items: [
                    { latex: '\\sin(#?)', display: 'sin(□)' },
                    { latex: '\\cos(#?)', display: 'cos(□)' },
                    { latex: '\\tan(#?)', display: 'tan(□)' },
                    { latex: '\\sec(#?)', display: 'sec(□)' },
                    { latex: '\\csc(#?)', display: 'csc(□)' },
                    { latex: '\\cot(#?)', display: 'cot(□)' },
                    { latex: '\\arcsin(#?)', display: 'asin(□)' },
                    { latex: '\\arccos(#?)', display: 'acos(□)' },
                    { latex: '\\arctan(#?)', display: 'atan(□)' },
                    { latex: '\\sinh(#?)', display: 'sinh(□)' },
                    { latex: '\\cosh(#?)', display: 'cosh(□)' },
                    { latex: '\\tanh(#?)', display: 'tanh(□)' },
                    { latex: '\\ln(#?)', display: 'ln(□)' },
                    { latex: '\\log_{#?}(#?)', display: 'logₐ(□)' },
                    { latex: '\\exp(#?)', display: 'exp(□)' },
                    { latex: '\\max', display: 'max' },
                    { latex: '\\min', display: 'min' },
                    { latex: '\\sup', display: 'sup' },
                    { latex: '\\inf', display: 'inf' },
                ]
            },
            {
                name: 'Calculus',
                items: [
                    { latex: '\\int', display: '∫' },
                    { latex: '\\int_{#?}^{#?}', display: '∫ₐᵇ' },
                    { latex: '\\iint', display: '∬' },
                    { latex: '\\iiint', display: '∭' },
                    { latex: '\\oint', display: '∮' },
                    { latex: '\\sum_{#?}^{#?}', display: '∑' },
                    { latex: '\\prod_{#?}^{#?}', display: '∏' },
                    { latex: '\\lim_{#? \\to #?}', display: 'lim' },
                    { latex: '\\frac{d}{dx}', display: 'd/dx' },
                    { latex: '\\partial', display: '∂' },
                    { latex: '\\nabla', display: '∇' },
                    { latex: '\\infty', display: '∞' },
                ]
            }
        ]
    },
    'Letters': {
        icon: <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
            <Typography sx={{ fontSize: '18px', fontFamily: 'serif' }}>α</Typography>
            <Typography sx={{ fontSize: '16px', fontFamily: 'serif' }}>β</Typography>
        </Box>,
        sections: [
            {
                name: 'Lowercase Greek',
                items: [
                    { latex: '\\alpha', display: 'α' },
                    { latex: '\\beta', display: 'β' },
                    { latex: '\\gamma', display: 'γ' },
                    { latex: '\\delta', display: 'δ' },
                    { latex: '\\epsilon', display: 'ε' },
                    { latex: '\\varepsilon', display: 'ε' },
                    { latex: '\\zeta', display: 'ζ' },
                    { latex: '\\eta', display: 'η' },
                    { latex: '\\theta', display: 'θ' },
                    { latex: '\\vartheta', display: 'ϑ' },
                    { latex: '\\iota', display: 'ι' },
                    { latex: '\\kappa', display: 'κ' },
                    { latex: '\\lambda', display: 'λ' },
                    { latex: '\\mu', display: 'μ' },
                    { latex: '\\nu', display: 'ν' },
                    { latex: '\\xi', display: 'ξ' },
                    { latex: '\\pi', display: 'π' },
                    { latex: '\\varpi', display: 'ϖ' },
                    { latex: '\\rho', display: 'ρ' },
                    { latex: '\\varrho', display: 'ϱ' },
                    { latex: '\\sigma', display: 'σ' },
                    { latex: '\\varsigma', display: 'ς' },
                    { latex: '\\tau', display: 'τ' },
                    { latex: '\\upsilon', display: 'υ' },
                    { latex: '\\phi', display: 'φ' },
                    { latex: '\\varphi', display: 'φ' },
                    { latex: '\\chi', display: 'χ' },
                    { latex: '\\psi', display: 'ψ' },
                    { latex: '\\omega', display: 'ω' },
                ]
            },
            {
                name: 'Uppercase Greek',
                items: [
                    { latex: '\\Gamma', display: 'Γ' },
                    { latex: '\\Delta', display: 'Δ' },
                    { latex: '\\Theta', display: 'Θ' },
                    { latex: '\\Lambda', display: 'Λ' },
                    { latex: '\\Xi', display: 'Ξ' },
                    { latex: '\\Pi', display: 'Π' },
                    { latex: '\\Sigma', display: 'Σ' },
                    { latex: '\\Phi', display: 'Φ' },
                    { latex: '\\Psi', display: 'Ψ' },
                    { latex: '\\Omega', display: 'Ω' },
                ]
            }
        ]
    },
    'Logic & Set': {
        icon: <Layers size={20} strokeWidth={1.5} />,
        sections: [
            {
                name: 'Logic Operators',
                items: [
                    { latex: '\\forall', display: '∀' },
                    { latex: '\\exists', display: '∃' },
                    { latex: '\\neg', display: '¬' },
                    { latex: '\\wedge', display: '∧' },
                    { latex: '\\vee', display: '∨' },
                    { latex: '\\implies', display: '⇒' },
                    { latex: '\\iff', display: '⇔' },
                    { latex: '\\therefore', display: '∴' },
                    { latex: '\\because', display: '∵' },
                    { latex: '\\in', display: '∈' },
                    { latex: '\\notin', display: '∉' },
                ]
            },
            {
                name: 'Set Theory',
                items: [
                    { latex: '\\cup', display: '∪' },
                    { latex: '\\cap', display: '∩' },
                    { latex: '\\subset', display: '⊂' },
                    { latex: '\\supset', display: '⊃' },
                    { latex: '\\subseteq', display: '⊆' },
                    { latex: '\\supseteq', display: '⊇' },
                    { latex: '\\setminus', display: '∖' },
                    { latex: '\\emptyset', display: '∅' },
                    { latex: '\\mathbb{R}', display: 'ℝ' },
                    { latex: '\\mathbb{N}', display: 'ℕ' },
                    { latex: '\\mathbb{Z}', display: 'ℤ' },
                    { latex: '\\mathbb{Q}', display: 'ℚ' },
                    { latex: '\\mathbb{C}', display: 'ℂ' },
                    { latex: '\\mathcal{P}', display: 'P' },
                    { latex: '\\wp', display: '℘' },
                    { latex: '\\complement', display: '∁' },
                ]
            }
        ]
    },
    'Arrows': {
        icon: <ArrowLeftRight size={20} strokeWidth={1.5} />,
        sections: [
            {
                name: 'Directional Arrows',
                items: [
                    { latex: '\\leftarrow', display: '←' },
                    { latex: '\\rightarrow', display: '→' },
                    { latex: '\\uparrow', display: '↑' },
                    { latex: '\\downarrow', display: '↓' },
                    { latex: '\\leftrightarrow', display: '↔' },
                    { latex: '\\updownarrow', display: '↕' },
                    { latex: '\\Leftarrow', display: '⇐' },
                    { latex: '\\Rightarrow', display: '⇒' },
                    { latex: '\\Uparrow', display: '⇑' },
                    { latex: '\\Downarrow', display: '⇓' },
                    { latex: '\\Leftrightarrow', display: '⇔' },
                    { latex: '\\mapsto', display: '↦' },
                    { latex: '\\leadsto', display: '⇝' },
                ]
            }
        ]
    },
    'Accents': {
        icon: <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Minus size={12} strokeWidth={3} style={{ marginBottom: -4 }} />
            <Typography sx={{ fontSize: '16px', fontFamily: 'serif' }}>U</Typography>
        </Box>,
        sections: [
            {
                name: 'Variable Accents',
                items: [
                    { latex: '\\bar{#?}', display: 'x̄' },
                    { latex: '\\hat{#?}', display: 'x̂' },
                    { latex: '\\vec{#?}', display: 'x⃗' },
                    { latex: '\\dot{#?}', display: 'ẋ' },
                    { latex: '\\ddot{#?}', display: 'ẍ' },
                    { latex: '\\tilde{#?}', display: 'x̃' },
                    { latex: '\\breve{#?}', display: 'x̆' },
                    { latex: '\\check{#?}', display: 'x̌' },
                ]
            }
        ]
    },
    'Delimiters': {
        icon: <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <Typography sx={{ fontSize: '16px' }}>[</Typography>
            <Typography sx={{ fontSize: '12px', color: '#64748b' }}>...</Typography>
            <Typography sx={{ fontSize: '16px' }}>]</Typography>
        </Box>,
        sections: [
            {
                name: 'Brackets & Grouping',
                items: [
                    { latex: '( )', display: '( )' },
                    { latex: '[ ]', display: '[ ]' },
                    { latex: '\\{ \\}', display: '{ }' },
                    { latex: '| |', display: '| |' },
                    { latex: '\\| \\|', display: '‖ ‖' },
                    { latex: '\\lfloor \\rfloor', display: '⌊ ⌋' },
                    { latex: '\\lceil \\rceil', display: '⌈ ⌉' },
                    { latex: '\\langle \\rangle', display: '⟨ ⟩' },
                ]
            }
        ]
    },
    'Arrays': {
        icon: <Grid3X3 size={20} strokeWidth={1.5} />,
        sections: [
            {
                name: 'Matrices',
                items: [
                    { latex: '\\begin{matrix} #? & #? \\\\ #? & #? \\end{matrix}', display: '□ □\n□ □' },
                    { latex: '\\begin{pmatrix} #? & #? \\\\ #? & #? \\end{pmatrix}', display: '(□ □\n□ □)' },
                    { latex: '\\begin{bmatrix} #? & #? \\\\ #? & #? \\end{bmatrix}', display: '[□ □\n□ □]' },
                    { latex: '\\begin{Bmatrix} #? & #? \\\\ #? & #? \\end{Bmatrix}', display: '{□ □\n□ □}' },
                    { latex: '\\begin{vmatrix} #? & #? \\\\ #? & #? \\end{vmatrix}', display: '|□ □\n□ □|' },
                    { latex: '\\begin{Vmatrix} #? & #? \\\\ #? & #? \\end{Vmatrix}', display: '||□ □\n□ □||' },
                    { latex: '\\begin{cases} #? & #? \\\\ #? & #? \\end{cases}', display: '{□ □\n□ □' },
                    { latex: '\\begin{matrix} #? & #? \\\\ #? & #? \\end{matrix}', display: 'small\n2x2' },
                    { latex: '\\begin{pmatrix} #? \\\\ #? \\end{pmatrix}', display: '(□\n□)' },
                ]
            }
        ]
    },
    'Constants': {
        icon: <Ruler size={20} strokeWidth={1.5} />,
        sections: [
            {
                name: 'Symbols & Constants',
                items: [
                    { latex: '\\pi', display: 'π' },
                    { latex: 'e', display: 'e' },
                    { latex: 'i', display: 'i' },
                    { latex: '\\phi', display: 'φ' },
                    { latex: '\\infty', display: '∞' },
                    { latex: '\\hbar', display: 'ℏ' },
                    { latex: 'c', display: 'c' },
                    { latex: 'G', display: 'G' },
                    { latex: 'h', display: 'h' },
                    { latex: 'k', display: 'k' },
                    { latex: 'N_A', display: 'Nₐ' },
                    { latex: '\\mu_0', display: 'μ₀' },
                    { latex: '\\epsilon_0', display: 'ε₀' },
                    { latex: '\\sigma', display: 'σ' },
                ]
            }
        ]
    },
    'Special': {
        icon: <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontFamily: 'serif' }}>∎</Typography>
        </Box>,
        sections: [
            {
                name: 'Proof & Logic',
                items: [
                    { latex: '\\blacksquare', display: '■' },
                    { latex: '\\square', display: '□' },
                    { latex: '\\lozenge', display: '◊' },
                    { latex: '\\blacklozenge', display: '◆' },
                    { latex: '\\triangle', display: '△' },
                    { latex: '\\blacktriangle', display: '▲' },
                    { latex: '\\triangledown', display: '▽' },
                    { latex: '\\blacktriangledown', display: '▼' },
                    { latex: '\\diamond', display: '⋄' },
                    { latex: '\\nabla', display: '∇' },
                ]
            },
            {
                name: 'Geometry',
                items: [
                    { latex: '\\angle', display: '∠' },
                    { latex: '\\measuredangle', display: '∡' },
                    { latex: '\\sphericalangle', display: '∢' },
                    { latex: '\\perp', display: '⊥' },
                    { latex: '\\parallel', display: '∥' },
                    { latex: '\\circ', display: '∘' },
                    { latex: '\\bigcirc', display: '○' },
                    { latex: '\\bullet', display: '∙' },
                    { latex: '\\star', display: '⋆' },
                ]
            }
        ]
    }
};

interface SymbolLibraryProps {
    open: boolean;
    onClose: () => void;
    onSelect: (latex: string) => void;
}

const SymbolLibrary: React.FC<SymbolLibraryProps> = ({ open, onClose, onSelect }) => {
    const [activeTab, setActiveTab] = useState('Basic');
    const [searchQuery, setSearchQuery] = useState('');
    const [recentSymbols, setRecentSymbols] = useState<SymbolItem[]>([]);

    useEffect(() => {
        if (open) {
            const recent = JSON.parse(localStorage.getItem('recent_symbols') || '[]');
            setRecentSymbols(recent.map((s: string) => ({ latex: s, display: s })));
        }
    }, [open]);

    const dynamicSymbolData = useMemo(() => {
        const data = { ...symbolData };
        if (recentSymbols.length > 0) {
            data['Recent'].sections[0].items = recentSymbols;
        }
        return data;
    }, [recentSymbols]);

    const allSymbols = useMemo(() => {
        const list: SymbolItem[] = [];
        Object.entries(dynamicSymbolData).forEach(([catKey, cat]) => {
            cat.sections.forEach(sec => {
                sec.items.forEach(item => {
                    list.push({ ...item, keywords: [sec.name.toLowerCase(), catKey.toLowerCase()] });
                });
            });
        });
        return list;
    }, [dynamicSymbolData]);

    const filteredSymbols = useMemo(() => {
        if (!searchQuery) return null;
        return allSymbols.filter(s =>
            s.latex.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.display.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.keywords?.some(k => k.includes(searchQuery.toLowerCase()))
        );
    }, [searchQuery, allSymbols]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            PaperProps={{
                className: 'symbol-library-paper'
            }}
        >
            <DialogTitle
                className="symbol-library-header"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography variant="h6" className="header-title">Math Library</Typography>
                    <Paper
                        className="library-search-box"
                        elevation={0}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Search size={16} color="#64748b" />
                        <InputBase
                            placeholder="Search symbols (e.g. sigma, alpha)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{ ml: 1, flex: 1, fontSize: '0.875rem' }}
                            autoFocus={false}
                        />
                    </Paper>
                    <IconButton onClick={onClose} size="small" className="close-btn" sx={{ ml: 'auto' }}>
                        <X size={20} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {!searchQuery && (
                <Box
                    className="tab-container"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <Tabs
                        value={activeTab}
                        onChange={(_, val) => setActiveTab(val)}
                        variant="scrollable"
                        scrollButtons="auto"
                        className="symbol-tabs"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        {Object.keys(dynamicSymbolData).map(key => (
                            <Tab
                                key={key}
                                value={key}
                                icon={dynamicSymbolData[key].icon}
                                label={key}
                                iconPosition="top"
                                className="symbol-tab"
                            />
                        ))}
                    </Tabs>
                </Box>
            )}

            <DialogContent
                className="symbol-grid-content"
                onMouseDown={(e) => e.stopPropagation()}
            >
                {searchQuery ? (
                    <Box>
                        <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#94a3b8' }}>
                            Found {filteredSymbols?.length} matches
                        </Typography>
                        <Grid container spacing={1.5}>
                            {filteredSymbols?.map((item, idx) => (
                                <Grid item key={idx} sx={{ display: 'flex' }}>
                                    <Box
                                        className="symbol-button"
                                        onClick={() => { onSelect(item.latex); onClose(); }}
                                        sx={{ flex: 1 }}
                                    >
                                        <span className="symbol-tex" style={{ fontSize: item.display.length > 3 ? '0.9rem' : '1.25rem' }}>{item.display}</span>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ) : (
                    dynamicSymbolData[activeTab].sections.map((section, sIdx) => (
                        <Box key={sIdx} sx={{ mb: 4 }}>
                            <Typography variant="subtitle2" className="section-header" sx={{ mb: 2, color: '#64748b', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                {section.name}
                            </Typography>
                            <Grid container spacing={1.5}>
                                {section.items.map((item, idx) => (
                                    <Grid item key={idx}>
                                        <Box className="symbol-button" onClick={() => { onSelect(item.latex); onClose(); }}>
                                            <span className="symbol-tex" style={{ fontSize: item.display.length > 3 ? '0.9rem' : '1.25rem' }}>{item.display}</span>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SymbolLibrary;
